import { useTranslation } from '../contexts/TranslationContext';
import { PublicFaqItem } from './PublicFaqItem';
import { LANDING_SECTION_TITLE_CLASS_NAME, landingSectionClassName } from './landingSectionClassName';
import {
  AI_RELIABILITY_PATH,
  ASSISTANT_ADR_PATH,
  SECURITY_TRUST_PATH,
  localizePublicHref,
} from '../utils/publicRoutes';
import { BrandLink } from './ui/BrandButton';
import {
  LANDING_FAQ_CREDIT_OPTION_KEYS,
  LANDING_FAQ_QUESTION_KEYS,
  LANDING_FAQ_QUESTION_TYPE_KEYS,
} from '../config/landingFaq';

const OWASP_TOP_TEN_URL = 'https://owasp.org/www-project-top-ten/';

interface LandingQaContentProps { visibleSections: Set<string>; }

export function LandingQaContent({
  visibleSections,
}: LandingQaContentProps) {
  const { language, t } = useTranslation();
  const questionKeys = LANDING_FAQ_QUESTION_KEYS;

  return (
    <section id="faq" className={landingSectionClassName({ className: 'px-4 py-14 sm:px-6 sm:py-32' })}>
      <div className="mx-auto w-full min-w-0 max-w-6xl">
        <div
          id="faq-header"
          data-landing-scroll-target
          data-animate
          className={`mb-2 w-full text-center transition-all duration-1000 sm:mb-3 ${
            visibleSections.has('faq-header') ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <h2 className={LANDING_SECTION_TITLE_CLASS_NAME}>
            {t('qa.title')}
          </h2>
        </div>

        <div
          id="faq-list"
          data-animate
          className={`mx-auto flex w-full max-w-4xl flex-col gap-3 transition-all duration-1000 ${
            visibleSections.has('faq-list') ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          {questionKeys.map((questionKey) => (
            <PublicFaqItem
              key={questionKey}
              id={`faq-${questionKey}`}
              question={t(`qa.${questionKey}.question`)}
            >
              <p>
                {questionKey === 'dataProtection' ? (
                  <>
                    {t('qa.dataProtection.answer1').split('OWASP Top 10')[0]}
                    <a
                      href={OWASP_TOP_TEN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="brand-focus-ring font-semibold underline decoration-current/50 underline-offset-4 hover:no-underline"
                    >
                      OWASP Top 10
                    </a>
                    {t('qa.dataProtection.answer1').split('OWASP Top 10')[1]}
                  </>
                ) : t(`qa.${questionKey}.answer1`)}
              </p>
              {questionKey === 'trialEnd' && (
                <>
                  <ol className="list-decimal space-y-2 pl-5">
                    {LANDING_FAQ_CREDIT_OPTION_KEYS.map((option) => (
                      <li key={option}>{t(`qa.trialEnd.items.${option}`)}</li>
                    ))}
                  </ol>
                  <p className="text-sm leading-6">{t('qa.trialEnd.answer2')}</p>
                </>
              )}
              {questionKey === 'aiAgentGuide' ? (
                <div className="flex justify-center">
                  <BrandLink href={localizePublicHref(ASSISTANT_ADR_PATH, language)} className="max-w-full whitespace-normal text-center leading-5">
                    {t('qa.aiAgentGuide.linkLabel')}
                  </BrandLink>
                </div>
              ) : null}
              {questionKey === 'chatGptDifference' ? (
                <div className="flex justify-center">
                  <BrandLink href={localizePublicHref(AI_RELIABILITY_PATH, language)} className="max-w-full whitespace-normal text-center leading-5">
                    {t('impact.reliabilityLink')}
                  </BrandLink>
                </div>
              ) : null}
              {questionKey === 'questionTypes' ? (
                <>
                  <p>{t('qa.questionTypes.answer2')}</p>
                  <ul className="list-disc space-y-2 pl-5">
                    {LANDING_FAQ_QUESTION_TYPE_KEYS.map((itemKey) => (
                      <li key={itemKey} className="brand-marker pl-1">
                        <span className="break-words">{t(`qa.questionTypes.items.${itemKey}`)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {questionKey === 'reliability' ? (
                <>
                  <p>{t('qa.reliability.answer2')}</p>
                  <p>{t('qa.reliability.answer3')}</p>
                  <p>{t('qa.reliability.answer4')}</p>
                  <div className="flex justify-center">
                    <BrandLink href={localizePublicHref(AI_RELIABILITY_PATH, language)} className="max-w-full whitespace-normal text-center leading-5">
                      {t('impact.reliabilityLink')}
                    </BrandLink>
                  </div>
                </>
              ) : null}
              {questionKey === 'dataProtection' ? (
                <>
                  <p>{t('qa.dataProtection.answer2')}</p>
                  <p>{t('qa.dataProtection.answer3')}</p>
                  <div className="flex justify-center">
                    <BrandLink href={localizePublicHref(SECURITY_TRUST_PATH, language)} className="max-w-full whitespace-normal text-center leading-5">
                      {t('qa.dataProtection.linkLabel')}
                    </BrandLink>
                  </div>
                </>
              ) : null}
            </PublicFaqItem>
          ))}
        </div>
      </div>
    </section>
  );
}
